import React from 'react';
import { RealizedSavings } from '../../types/dashboard';
import { CurrencyCode } from '../../utils/currency';

/** Narrow an arbitrary currency string to a known CurrencyCode, defaulting to USD */
function toCurrencyCode(currency: string): CurrencyCode {
  return (['USD', 'INR', 'EUR', 'GBP'] as const).includes(currency as CurrencyCode)
    ? (currency as CurrencyCode)
    : 'USD';
}

/**
 * SavingsBreakdown component for displaying realized savings
 * AC 1: Display realized savings
 * WCAG 2.1 AA compliant with accessible charts
 */

interface SavingsBreakdownProps {
  savings: RealizedSavings;
  /** Currency to display amounts in (native currency for single-provider views, USD for All Providers) */
  currency?: string;
}

export const SavingsBreakdown: React.FC<SavingsBreakdownProps> = ({ savings, currency = 'USD' }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: toCurrencyCode(currency),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Generate colors for pie chart segments
  const colors = [
    'bg-primary dark:bg-primary',
    'bg-emerald-500 dark:bg-emerald-600',
    'bg-amber-500 dark:bg-amber-600',
    'bg-sky-500 dark:bg-sky-600',
    'bg-sky-500 dark:bg-sky-600',
  ];

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Potential Savings</h3>

      {/* Total Savings */}
      <div className="mb-4 text-center">
        <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(savings.totalSavings)}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          {savings.savingsPercentage.toFixed(1)}% of spend is recoverable
        </div>
      </div>

      {/* Savings by Category */}
      <div className="mb-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground/80">By Category</h4>
        {savings.savingsByCategory.map((category, index) => (
          <div key={index} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div
                  className={`h-3 w-3 rounded-sm ${colors[index % colors.length]}`}
                  aria-hidden="true"
                />
                <span className="font-medium text-foreground/80">
                  {category.category}
                </span>
              </div>
              <span className="font-semibold text-foreground">
                {formatCurrency(category.amount)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"
                role="progressbar"
                aria-valuenow={category.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${category.category}: ${category.percentage.toFixed(1)}%`}
              >
                <div
                  className={`h-full transition-all ${colors[index % colors.length]}`}
                  style={{ width: `${category.percentage}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-12 text-right">
                {category.percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly Trend */}
      {savings.monthlyTrend.length > 0 ? (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-foreground/80">Monthly Trend</h4>
          <div className="flex items-end justify-between gap-1" style={{ height: '80px' }}>
            {savings.monthlyTrend.slice(-6).map((month, index) => {
              const maxValue = Math.max(...savings.monthlyTrend.map((m) => m.amount));
              const height = (month.amount / maxValue) * 80;

              return (
                <div key={index} className="flex flex-1 flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t bg-emerald-500 transition-all hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                    style={{ height: `${height}px` }}
                    title={`${month.month}: ${formatCurrency(month.amount)}`}
                    aria-label={`${month.month}: ${formatCurrency(month.amount)} savings`}
                  />
                  <span className="text-xs text-muted-foreground">
                    {month.month.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Not enough monthly history yet to chart a trend.</p>
      )}

      {/* Accessible data table (visually hidden) */}
      <table className="sr-only" aria-label="Savings by category data table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Amount</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
          {savings.savingsByCategory.map((category, index) => (
            <tr key={index}>
              <td>{category.category}</td>
              <td>{formatCurrency(category.amount)}</td>
              <td>{category.percentage.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
